/**
 * The upload date filter's shape and how it turns into API bounds.
 */

export type DatePreset = 'today' | 'month' | 'year' | 'custom';

export interface DateRange {
  preset: DatePreset;
  /** yyyy-mm-dd, only meaningful for custom. */
  from: string;
  to: string;
}

export const PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'month', label: 'This month' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
];

export const EMPTY_RANGE: DateRange = { preset: 'today', from: '', to: '' };

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

/** A yyyy-mm-dd string as a local date, or null when it is not one. */
function parseLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** ISO bounds for the API, or nulls when the range does not apply. */
export function dateBounds(range: DateRange | null): { since: string | null; until: string | null } {
  if (!range) return { since: null, until: null };
  const now = new Date();
  switch (range.preset) {
    case 'today':
      return { since: startOfDay(now).toISOString(), until: null };
    case 'month':
      return { since: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), until: null };
    case 'year':
      return { since: new Date(now.getFullYear(), 0, 1).toISOString(), until: null };
    case 'custom': {
      const from = parseLocal(range.from);
      const to = parseLocal(range.to);
      return {
        since: from ? startOfDay(from).toISOString() : null,
        until: to ? endOfDay(to).toISOString() : null,
      };
    }
  }
}

function shortLocal(value: string): string {
  const date = parseLocal(value);
  return date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : value;
}

/** What the chip says once a range is applied. */
export function dateLabel(range: DateRange | null): string {
  if (!range) return 'Upload date';
  if (range.preset !== 'custom') return PRESETS.find((p) => p.value === range.preset)?.label ?? 'Upload date';
  if (range.from && range.to) return `${shortLocal(range.from)} to ${shortLocal(range.to)}`;
  if (range.from) return `From ${shortLocal(range.from)}`;
  if (range.to) return `Until ${shortLocal(range.to)}`;
  return 'Upload date';
}
